import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {ErrorBoundary} from '@sentry/react';
import {getAllActionTypes, getAllCategories} from 'src/components/change-log/utils';
import {FilterInput} from 'src/components/input';
import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {ChangeLogDrawer} from 'src/modules/change-log';
import {NoResultPage} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {getUserLogById} from 'src/openapi/admin/apis/UserLogApi';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';
import {pluralize} from '@pb/kit-formatters';

import {SECTION_NAME} from './constants';
import {FilterStrip} from './filter/filter-strip';
import {LogFiltersBody} from './filter/log-filters-body';
import {useGetUserAuditLog} from './hooks/useGetUserAuditLog';
import {useGetUserLogActionDictionary} from './hooks/useGetUserLogActionDictionary';
import {userAuditLogStateActions} from './slices/user-audit-log-state.slice';
import {userAuditLogActions} from './slices/user-audit-log.slice';
import {UserLogTable} from './user-log-table';

import {
    userAuditLogHeader,
    userAuditLogLoadingContainer,
    userAuditLogSectionTitle,
    userAuditLogSeparator,
    userAuditLogTotalCount,
} from './user-audit-log.module.styl';

import type {UserAuditLogCriteria} from './typedef';
import type {ApiDispatchForToolkit} from 'src/openapi/admin/types';


type Props = {
    userId: number,
};

export const UserAuditLog = ({userId}: Props) => {
    const dispatch = useDispatch();
    const apiDispatch = useDispatch<ApiDispatchForToolkit>();
    const [selectedLogId, setSelectedLogId] = useState<string | null>(null);
    // const {userAuditLogSubmit} = useEventTracking(); TODO: add tracking

    const {loadingError, result, cacheKeys} = useGetUserAuditLog({userId});
    const {
        data: actionTypeDictionary,
        loadingError: actionTypeDictionaryLoadingError,
        isLoading: actionTypeDictionaryIsLoading,
    } = useGetUserLogActionDictionary({userId});

    const noUserResultMessage = useMemo(() => {
        if (Object.keys(cacheKeys.filters).length && !result?.data.length) {
            return (
                <NoResultPage
                    title="No data found for the selected criteria."
                    description=""
                />
            );
        } else if (!Object.keys(cacheKeys.filters).length) {
            return (
                <NoResultPage
                    title="No data to show."
                    description=""
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    const changeSearchQuery = useCallback((value: string) => {
        dispatch(userAuditLogStateActions.setFilters({
            ...cacheKeys.filters,
            searchQuery: value,
        }));
    }, [cacheKeys.filters, dispatch]);

    const clearSearchQuery = useCallback(() => {
        const {searchQuery, ...otherFilters} = cacheKeys.filters;
        dispatch(userAuditLogStateActions.setFilters(otherFilters));
    }, [cacheKeys.filters, dispatch]);

    const applyFilters = useCallback((filters: UserAuditLogCriteria) => {
        dispatch(userAuditLogStateActions.setFilters(filters));
        // userAuditLogSubmit({
        //     actionType: 'Apply Filters',
        //     filterCriteria: Object
        //         .entries(filters)
        //         .map(([key, value]) => `${key}: ${isArray(value) ? value.join(', ') : value}`),
        // }); TODO: Add tracking
    }, [dispatch]);

    const clearFilters = useCallback(() => {
        const {searchQuery} = cacheKeys.filters;
        // userAuditLogSubmit({
        //     actionType: 'Reset',
        //     filterCriteria: Object
        //         .entries(cacheKeys.filters)
        //         .map(([key, value]) => `${key}: ${isArray(value) ? value.join(', ') : value}`),
        // }); TODO: Add tracking

        const clearedFilters: UserAuditLogCriteria = searchQuery ? {
            searchQuery: searchQuery as string,
        } : {};

        dispatch(userAuditLogStateActions.setFilters(clearedFilters));
        return clearedFilters;
    }, [cacheKeys.filters, dispatch]);

    const closeDrawer = useCallback(() => {
        setSelectedLogId(null);
    }, []);

    const loader = useCallback(
        (logId: string) => apiDispatch(getUserLogById({logId})),
        [apiDispatch],
    );

    const allActionTypes = useMemo(() => {
        if (actionTypeDictionaryIsLoading) {
            return [];
        }

        return getAllActionTypes(actionTypeDictionary);
    }, [actionTypeDictionary, actionTypeDictionaryIsLoading]);

    const allCategories = useMemo(() => {
        if (actionTypeDictionaryIsLoading) {
            return [];
        }

        return getAllCategories(actionTypeDictionary);
    }, [actionTypeDictionary, actionTypeDictionaryIsLoading]);


    const filtersCount = useMemo(() => {
        return Object.keys(cacheKeys.filters).filter(item => item !== 'searchQuery').length;
    }, [cacheKeys.filters]);

    const clearAllDisabled = useMemo(() => {
        return !Object.keys(cacheKeys.filters).length;
    }, [cacheKeys.filters]);

    useEffect(() => {
        return () => {
            dispatch(userAuditLogActions.clearData());
            dispatch(userAuditLogStateActions.clearData());
        };
    }, [dispatch]);

    return (
        <ServerResponseHandler
            serverError={loadingError || actionTypeDictionaryLoadingError}
            sectionName={SECTION_NAME}>
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <StickyElement id="manage-controls">
                    <div className={userAuditLogHeader}>
                        <header className={userAuditLogSectionTitle}>
                            <Group direction="h" alignItems="center">
                                <Typography
                                    fontWeight="semi-bold"
                                    variant="heading-level-3">
                                    Audit Log
                                </Typography>
                                {result?.totalCount ? (
                                    <>
                                        <span className={userAuditLogSeparator}/>
                                        <span className={userAuditLogTotalCount}>
                                            {pluralize(`${result.totalCount} Action[s]`, result.totalCount)}
                                        </span>
                                    </>
                                ) : null}
                            </Group>
                        </header>
                        {!(!result?.totalCount && !Object.keys(cacheKeys.filters).length) ? (
                            <Group direction="v" gap="m" as="section">
                                <Group direction="h" gap="m" as="section">
                                    <FilterInput
                                        id="user-audit-log-filter"
                                        placeholder="Search for actions by description"
                                        onChange={changeSearchQuery}
                                        onClear={clearSearchQuery}
                                        timeOut={1000}
                                        width={600}
                                        value={(cacheKeys.filters?.searchQuery || '') as string}
                                    />
                                    <SectionFilter<UserAuditLogCriteria>
                                        onApply={applyFilters}
                                        maxHeight={500}
                                        width={465}
                                        onClear={clearFilters}
                                        filters={cacheKeys.filters as UserAuditLogCriteria}
                                        getAnchor={({anchorRef, positionRef, getAnchorProps}) => (
                                            <Button
                                                variant="Type.SECONDARY"
                                                startIcon={(
                                                    <Icon
                                                        type={filtersCount ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY}
                                                    />
                                                )}
                                                endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                                ref={anchorRef}
                                                {...getAnchorProps()}>
                                                <b>Filter</b>
                                            </Button>
                                        )}
                                        defaultFilters={{}}>
                                        {(filter, onChange) => (
                                            <LogFiltersBody
                                                filter={filter}
                                                onChange={onChange}
                                                actionTypeDictionary={actionTypeDictionary}
                                            />
                                        )}
                                    </SectionFilter>
                                </Group>
                                <FilterStrip
                                    clearAllDisabled={clearAllDisabled}
                                    actionTypeDictionary={allActionTypes}
                                    categoryDictionary={allCategories}
                                />
                            </Group>
                        ) : null}
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    {result?.data.length ? (
                        <div className={userAuditLogLoadingContainer}>
                            <UserLogTable
                                onLogClick={setSelectedLogId}
                                rows={result?.data || []}
                                totalCount={result?.totalCount || 0}
                                pagination={cacheKeys.pagination}
                                actionTypeDictionary={allActionTypes}
                            />
                        </div>
                    ) : noUserResultMessage}
                </BoxLoading>
                <ChangeLogDrawer
                    selectedLogId={selectedLogId}
                    onClose={closeDrawer}
                    loader={loader}
                />
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
